import {
  ALLOWED_OPERATIONAL_BILLING_STATUSES,
  BILLING_STATUSES,
  LOAD_ORDER,
  LOAD_STATUS,
  TIMESTAMPD_STATUSES,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import moment from 'moment';
import { useCallback, useState } from 'react';

const getExistingTimestampForStatus = (
  st: LOAD_STATUS,
  loadObj: any,
): string | null => {
  if (!loadObj) return null;
  switch (st) {
    case LOAD_STATUS.PICKUP_GOING:
      return loadObj.pickupStartedOn || loadObj.loadStartedOn || null;
    case LOAD_STATUS.PICKUP_ARRIVED:
      return loadObj.pickupArrivedOn || loadObj.pickupCheckedinOn || null;
    case LOAD_STATUS.DROPOFF_GOING:
      return loadObj.dropoffStartedOn || loadObj.pickupDepartedOn || null;
    case LOAD_STATUS.DROPOFF_ARRIVED:
      return loadObj.dropoffArrivedOn || loadObj.dropoffCheckedinOn || null;
    case LOAD_STATUS.DELIVERED:
    case LOAD_STATUS.COMPLETED:
      return loadObj.dropoffCompletedOn || null;
    default:
      return null;
  }
};

export default function useStatusTransition(
  loadId?: number,
  currentStatus?: LOAD_STATUS,
  onStatusUpdated?: () => void,
) {
  const { rootStore } = useStores();
  const [openSelect, setOpenSelect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newStatus, setNewStatus] = useState<LOAD_STATUS | null>(null);

  // Modal States
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openPaperworkIssueModal, setOpenPaperworkIssueModal] = useState(false);
  const [openInvalidModal, setOpenInvalidModal] = useState(false);
  const [invalidMessage, setInvalidMessage] = useState('');

  // Timestamp Modal States
  const [openTimestampModal, setOpenTimestampModal] = useState(false);
  const [isManifestWideChange, setIsManifestWideChange] = useState(false);
  const [forwardStatusesToFill, setForwardStatusesToFill] = useState<
    LOAD_STATUS[]
  >([]);
  const [reverseStatusesToClear, setReverseStatusesToClear] = useState<
    LOAD_STATUS[]
  >([]);
  const [timestamps, setTimestamps] = useState<Record<string, string>>({});

  const handleCancel = useCallback(() => {
    setOpenCancelModal(false);
    setOpenPaperworkIssueModal(false);
    setOpenTimestampModal(false);
    setOpenInvalidModal(false);
    setOpenSelect(false);
    setNewStatus(null);
    setTimestamps({});
  }, []);

  const executeStatusUpdate = useCallback(
    async (
      targetStatus: LOAD_STATUS,
      description?: string | string[],
      customTimestamps?: Record<string, string>,
    ) => {
      if (!loadId) return;
      const load =
        rootStore.loadTableRows.get(`${loadId}`) || rootStore.trackedLeg;

      setIsLoading(true);
      if (load) {
        const payload: {
          status: LOAD_STATUS;
          description?: string;
          descriptions?: string[];
          timestamps?: Record<string, string>;
        } = {
          status: targetStatus,
          timestamps: customTimestamps || timestamps,
        };

        if (Array.isArray(description)) {
          payload.descriptions = description;
        } else if (typeof description === 'string') {
          payload.description = description;
        }

        const result = await rootStore.dispatcherUpdateLoadStatus(
          loadId,
          payload,
        );

        if (result && !result.ok) {
          const errorMsg =
            result.errors?.nonFieldErrors?.[0] ||
            result.errors?.status?.[0] ||
            result.errors?.detail ||
            'Cannot transition status. Please check required documents.';

          setInvalidMessage(errorMsg);
          setOpenInvalidModal(true);
          setIsLoading(false);
          return;
        }

        if (onStatusUpdated) onStatusUpdated();
      }

      handleCancel();
      setIsLoading(false);
    },
    [loadId, rootStore, timestamps, onStatusUpdated, handleCancel],
  );

  const handleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      event.stopPropagation();
      const selectedStatus = event.target.value as LOAD_STATUS;
      if (!currentStatus || selectedStatus === currentStatus) return;

      const isCurrentBilling = BILLING_STATUSES.includes(currentStatus);
      const isTargetBilling = BILLING_STATUSES.includes(selectedStatus);
      const isOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
        currentStatus,
      );
      const isTargetOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
        selectedStatus,
      );

      if (isCurrentBilling && !isTargetBilling && !isTargetOperationalAllowed) {
        setInvalidMessage(
          'From a Billing status, you can only switch to another Billing status, Delivered, or Dispatch Cleared.',
        );
        setOpenInvalidModal(true);
        return;
      }

      if (isTargetBilling && !isCurrentBilling && !isOperationalAllowed) {
        setInvalidMessage(
          'Switching to a Billing status is only allowed from Delivered or Dispatch Cleared status.',
        );
        setOpenInvalidModal(true);
        return;
      }

      setOpenSelect(false);
      setNewStatus(selectedStatus);

      if (selectedStatus === LOAD_STATUS.CANCELLED) {
        setOpenCancelModal(true);
        return;
      }
      if (selectedStatus === LOAD_STATUS.BILLING_ISSUE) {
        setOpenPaperworkIssueModal(true);
        return;
      }

      setIsManifestWideChange(isTargetBilling);

      const loadObj =
        rootStore.loadTableRows.get(`${loadId}`) || rootStore.trackedLeg;
      const currentIdx = LOAD_ORDER.indexOf(currentStatus);
      const targetIdx = LOAD_ORDER.indexOf(selectedStatus);

      if (currentIdx !== -1 && targetIdx !== -1) {
        const nowIso = moment().toISOString();

        if (targetIdx > currentIdx) {
          const requiredStatuses: LOAD_STATUS[] = [];
          const initialTs: Record<string, string> = {};

          for (let i = currentIdx + 1; i <= targetIdx; i++) {
            const st = LOAD_ORDER[i];
            let checkSt =
              st === LOAD_STATUS.COMPLETED ? LOAD_STATUS.DELIVERED : st;

            if (
              TIMESTAMPD_STATUSES.includes(checkSt) &&
              !requiredStatuses.includes(checkSt)
            ) {
              requiredStatuses.push(checkSt);
              const existingTs = getExistingTimestampForStatus(
                checkSt,
                loadObj,
              );
              initialTs[checkSt] = existingTs
                ? moment(existingTs).toISOString()
                : nowIso;
            }
          }

          setForwardStatusesToFill(requiredStatuses);
          setReverseStatusesToClear([]);
          setTimestamps(initialTs);
          setOpenTimestampModal(true);
          return;
        } else if (targetIdx < currentIdx) {
          const clearedStatuses: LOAD_STATUS[] = [];
          for (let i = targetIdx + 1; i <= currentIdx; i++) {
            const st = LOAD_ORDER[i];
            if (TIMESTAMPD_STATUSES.includes(st)) clearedStatuses.push(st);
          }

          const initialTs: Record<string, string> = {};
          let targetCheck =
            selectedStatus === LOAD_STATUS.COMPLETED
              ? LOAD_STATUS.DELIVERED
              : selectedStatus;

          if (TIMESTAMPD_STATUSES.includes(targetCheck)) {
            const existingTs = getExistingTimestampForStatus(
              targetCheck,
              loadObj,
            );
            initialTs[targetCheck] = existingTs
              ? moment(existingTs).toISOString()
              : nowIso;
          }

          setForwardStatusesToFill(
            TIMESTAMPD_STATUSES.includes(targetCheck) ? [targetCheck] : [],
          );
          setReverseStatusesToClear(clearedStatuses);
          setTimestamps(initialTs);
          setOpenTimestampModal(true);
          return;
        }
      }

      setForwardStatusesToFill([]);
      setReverseStatusesToClear([]);
      setOpenTimestampModal(true);
    },
    [currentStatus, loadId, rootStore],
  );

  return {
    openSelect,
    setOpenSelect,
    isLoading,
    newStatus,
    openCancelModal,
    openPaperworkIssueModal,
    openInvalidModal,
    invalidMessage,
    openTimestampModal,
    isManifestWideChange,
    forwardStatusesToFill,
    reverseStatusesToClear,
    timestamps,
    setTimestamps,
    handleChange,
    executeStatusUpdate,
    handleCancel,
  };
}
